import { connectCall } from '../gen/client'
import type {
  MeResponse,
  Review,
  StackEntry,
  Revision,
  Finding,
  Signal,
  TimelineEvent,
  Question,
  Answer,
  Memory,
  ListAuditLogResponse,
  ListPoliciesResponse,
  DecisionBrief,
  AuthorReplyItem,
  Reviewer,
  ListReviewersResponse,
  AssignableReviewer,
} from '../gen/types'

const REVIEW = 'samiksh.v1.ReviewService'
const EXPLAINER = 'samiksh.v1.ExplainerService'
const MEMORY = 'samiksh.v1.MemoryService'
const ADMIN = 'samiksh.v1.AdminService'

export const api = {
  me(): Promise<MeResponse> {
    return connectCall<Record<string, never>, MeResponse>(ADMIN, 'Me', {})
  },

  listReviews(params: {
    repo?: string
    status?: string
    limit?: number
    offset?: number
  }): Promise<{ reviews: Review[] }> {
    return connectCall(REVIEW, 'ListReviews', params)
  },

  getReview(reviewId: string): Promise<{ review: Review; stack: StackEntry[] }> {
    return connectCall(REVIEW, 'GetReview', { reviewId })
  },

  // ResolveReview maps the URL triplet (tenantName/repoName/reviewName) to the
  // underlying review uuid + the same payload shape as GetReview. The SPA
  // detail/diff pages call this once per page load, then use the returned
  // reviewId for every downstream RPC.
  resolveReview(tenantName: string, repoName: string, reviewName: string):
    Promise<{ reviewId: string; review: Review; stack: StackEntry[] }> {
    return connectCall(REVIEW, 'ResolveReview', { tenantName, repoName, reviewName })
  },

  listRevisions(reviewId: string): Promise<{ revisions: Revision[] }> {
    return connectCall(REVIEW, 'ListRevisions', { reviewId })
  },

  getRevision(revisionId: string): Promise<{ revision: Revision }> {
    return connectCall(REVIEW, 'GetRevision', { revisionId })
  },

  compareRevisions(
    reviewId: string,
    headRevisionId: string,
    baseRevisionId: string,
  ): Promise<{ baseSha: string; headSha: string; unifiedDiff: string; astDiffJson: string }> {
    return connectCall(REVIEW, 'CompareRevisions', { reviewId, headRevisionId, baseRevisionId })
  },

  listFindings(reviewId: string): Promise<{ findings: Finding[] }> {
    return connectCall(REVIEW, 'ListFindings', { reviewId })
  },

  updateFinding(
    reviewId: string,
    findingId: string,
    status: string,
  ): Promise<{ ok: boolean }> {
    return connectCall(REVIEW, 'UpdateFinding', { reviewId, findingId, status })
  },

  listSignals(reviewId: string): Promise<{ signals: Signal[] }> {
    return connectCall(REVIEW, 'ListSignals', { reviewId })
  },

  getTimeline(reviewId: string): Promise<{ events: TimelineEvent[] }> {
    return connectCall(REVIEW, 'GetTimeline', { reviewId })
  },

  getFileOverlay(
    reviewId: string,
    revisionId: string,
    path: string,
  ): Promise<{ events: TimelineEvent[] }> {
    return connectCall(REVIEW, 'GetFileOverlay', { reviewId, revisionId, path })
  },

  askQuestion(params: {
    reviewId: string
    revisionId?: string
    text: string
    scopeHint?: string
    anchorPath?: string
    anchorStartLine?: number
    anchorEndLine?: number
  }): Promise<{ question: Question; answer: Answer }> {
    return connectCall(EXPLAINER, 'AskQuestion', params)
  },

  listQuestions(reviewId: string): Promise<{ questions: Question[] }> {
    return connectCall(EXPLAINER, 'ListQuestions', { reviewId })
  },

  recordFeedback(
    answerId: string,
    positive: boolean,
    comment?: string,
  ): Promise<{ ok: boolean }> {
    return connectCall(EXPLAINER, 'RecordFeedback', { answerId, positive, comment })
  },

  humanDecision(
    reviewId: string,
    action: string,
    comment: string,
  ): Promise<{ ok: boolean }> {
    return connectCall(REVIEW, 'HumanDecision', { reviewId, action, comment })
  },

  listReviewers(reviewId: string): Promise<ListReviewersResponse> {
    return connectCall(REVIEW, 'ListReviewers', { reviewId })
  },

  listAssignableReviewers(reviewId: string): Promise<{ candidates: AssignableReviewer[] }> {
    return connectCall(REVIEW, 'ListAssignableReviewers', { reviewId })
  },

  assignReviewers(reviewId: string, githubLogins: string[]): Promise<{ reviewers: Reviewer[] }> {
    return connectCall(REVIEW, 'AssignReviewers', { reviewId, githubLogins })
  },

  unassignReviewer(reviewId: string, assignmentId: string): Promise<{ ok: boolean }> {
    return connectCall(REVIEW, 'UnassignReviewer', { reviewId, assignmentId })
  },

  submitReviewerDecision(
    reviewId: string,
    action: string,
    comment: string,
    mirrorToGithub = true,
  ): Promise<{ reviewer: Reviewer; reviewApproved: boolean; reviewBlocked: boolean }> {
    return connectCall(REVIEW, 'SubmitReviewerDecision', { reviewId, action, comment, mirrorToGithub })
  },

  authorReply(
    reviewId: string,
    replies: AuthorReplyItem[],
  ): Promise<{ ok: boolean; repliesSent: number }> {
    return connectCall(REVIEW, 'AuthorReply', { reviewId, replies })
  },

  postComment(
    reviewId: string,
    message: string,
    findingId?: string,
    anchor?: { path: string; revisionId: string; range: { startLine: number; endLine: number } },
  ): Promise<{ ok: boolean; eventId: string }> {
    return connectCall(REVIEW, 'PostComment', { reviewId, message, findingId, anchor })
  },

  listMemories(status?: string): Promise<{ memories: Memory[] }> {
    return connectCall(MEMORY, 'ListMemories', { status })
  },

  getDecisionBrief(reviewId: string): Promise<{ brief: DecisionBrief }> {
    return connectCall(REVIEW, 'GetDecisionBrief', { reviewId })
  },

  listAuditLog(params?: {
    pageSize?: number
    pageToken?: string
  }): Promise<ListAuditLogResponse> {
    return connectCall(ADMIN, 'ListAuditLog', params ?? {})
  },

  listPolicies(params?: {
    repo?: string
  }): Promise<ListPoliciesResponse> {
    return connectCall(ADMIN, 'ListPolicies', params ?? {})
  },
}
